import { message, Upload, UploadProps } from 'antd';
import dayjs from 'dayjs';
import i18n from 'i18next';

export const capitalizeName = (name: string | null | undefined) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export const formatFullName = (firstName: string | null | undefined, middleName: string | null | undefined, lastName: string | null | undefined) => {
    const first = capitalizeName(firstName)?.trim();
    const middle = capitalizeName(middleName)?.trim();
    const last = capitalizeName(lastName)?.trim();
    return [first, middle, last].filter(Boolean).join(' ');
};

export const maskName = (name: string | null | undefined): string => {
    if (!name) return '';

    const characters = Array.from(name);
    const length = characters.length;

    if (length === 1) {
        return name;
    }

    if (length === 2) {
        characters[1] = '*';
        return characters.join('');
    }

    if (length === 3) {
        characters[2] = '*';
        return characters.join('');
    }

    const startIdx = Math.floor(length / 2);
    for (let i = startIdx; i < length; i++) {
        characters[i] = '*';
    }

    return characters.join('');
};

export function transformNullToEmptyString(obj: Record<string, any>): Record<string, any> {
    return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];

        // Check if the value is a Day.js object
        if (dayjs.isDayjs(value)) {
            acc[key] = value; // Keep the Day.js object as is
        } else if (value === null) {
            acc[key] = ''; // Replace null with an empty string
        } else if (value === true) {
            acc[key] = 1; // Replace true with 1
        } else if (value === false) {
            acc[key] = 0; // Replace false with 0
        } else if (typeof value === 'object' && !Array.isArray(value)) {
            // If the value is an object (but not an array), recursively transform it
            acc[key] = transformNullToEmptyString(value);
        } else {
            acc[key] = value; // Keep the value as is
        }
        return acc;
    }, {} as Record<string, any>);
}

export const hasPermission = (tokenScopeList: any[], orgId: number, prefix: 'o' | 's', permission: string) => {
    const orgPermissionPrefix = `${prefix}${orgId}-`;
    const requiredPermission = orgPermissionPrefix + permission;

    // Filter the scopes that belong to the given orgId
    const orgScopes = tokenScopeList.find(scope => Number(scope.org.id) === Number(orgId));
    if (orgScopes) {
        return orgScopes.scope.includes(requiredPermission);
    }

    return false;
};

export const statusKeyMap: Record<string, string> = {
    'Pending': 'status.pending',
    'Signed In': 'status.signedIn',
    'Signed Out': 'status.signedOut',
    'Denied Entry': 'status.deniedEntry',
    'No Show': 'status.noShow',
};

export const resizeImage = (file: File, maxSize: number, quality: number): Promise<File | null> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target!.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d')!;

                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxSize) {
                        height *= maxSize / width;
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width *= maxSize / height;
                        height = maxSize;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            resolve(null);
                            return;
                        }
                        resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                    },
                    'image/jpeg',
                    quality
                );
            };
        };
    });
};

export const validateFile = async (file: File): Promise<File | null> => {
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
        message.error(i18n.t('util.notification.invalidFileError'));
        return null;
    }

    if (file.size > 1048576) {
        return await resizeImage(file, 600, 0.7);
    }

    return file;
};

export const beforeUpload = (sizeByte: number, sizeMegaByte: string): UploadProps['beforeUpload'] => {
    return async (file) => {
        if (file.size > sizeByte) {
            message.warning(i18n.t('util.notification.fileSizeWarning', {size: sizeMegaByte}));
            return Upload.LIST_IGNORE;
        }

        const validFile = await validateFile(file);
        return validFile ? false : Upload.LIST_IGNORE;
    };

};